import { ActionIcon, Box } from '@mantine/core';
import type { Filter } from '@repo/api-codegen';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import { EntityType } from '@repo/common/enums/entityType';
import { pluralize } from '@repo/common/utils';
import { Icon, Text } from '@repo/foundations';
import { Fragment, useCallback, useMemo, useState } from 'react';
import type { ISecodaEntity } from '../../../api';
import { useCatalogInfiniteList } from '../../../api/hooks/resourceCatalog';
import { useParamsIdSuffixUuid } from '../../../utils/hook/utils';
import { SmallLoadingSpinner } from '../../LoadingSpinner';
import { TREE_INDENT } from '../constants';
import type { CommonEntityTreeNode } from '../utils';
import { LoadMore } from './LoadMore';
import { useTreeNodeStyle } from './TreeNode.styles';

const defaultGetIcon = (node: CommonEntityTreeNode) => {
	if (node.entity.icon) {
		return <IconEmoji value={node.entity.icon} />;
	}
	if (node.entity.entity_type === EntityType.document) {
		return <Icon name="file" color="icon/primary/default" />;
	}
	if (node.entity.entity_type === EntityType.dictionary_term) {
		return <Icon name="book" color="icon/primary/default" />;
	}
	if (node.entity.entity_type === EntityType.glossary) {
		return <Icon name="book" color="icon/primary/default" />;
	}
	if (node.entity.entity_type === EntityType.bi_dashboard) {
		return <Icon name="customDashboard" color="icon/primary/default" />;
	}
	return null;
};

const TABLE_GROUP_ORDER = [
	'table',
	'dynamic_table',
	'view',
	'materialized_view',
	'task',
];

const formatGroupLabel = (nativeType: string) => {
	if (nativeType === 'unknown') {
		return 'Unknown';
	}

	const humanized = nativeType
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

	return pluralize(humanized);
};

export type TreeNodeBase = {
	hasChildren: boolean;
	id: string;
};

export interface EntityTreeNodeProps {
	node: CommonEntityTreeNode;
	level: number;
	getLabel: (node: CommonEntityTreeNode) => React.ReactNode;
	getIcon?: (node: CommonEntityTreeNode) => React.ReactNode;
	onClick: (e: React.MouseEvent, node: CommonEntityTreeNode) => void;
	childFilterOperands?: (node: CommonEntityTreeNode) => Filter[];
}

export function TreeNodeDisplay({
	node,
	level,
	getLabel,
	getIcon,
	onClick,
	open,
	isFetchingChildren,
	toggle,
	isSelected,
}: {
	node: CommonEntityTreeNode;
	level: number;
	getLabel: (node: CommonEntityTreeNode) => React.ReactNode;
	getIcon?: (node: CommonEntityTreeNode) => React.ReactNode;
	onClick: (e: React.MouseEvent, node: CommonEntityTreeNode) => void;
	open: boolean;
	isFetchingChildren: boolean;
	toggle: (e: React.MouseEvent) => void;
	isSelected: boolean;
}) {
	const { classes, theme } = useTreeNodeStyle({
		open: open,
		selected: isSelected,
	});

	return (
		<Box
			role="button"
			className={classes.button}
			pl={(level + 1) * TREE_INDENT - theme.other.space[1]}
			onClick={(e) => onClick(e, node)}
			data-testid={`tree-node-${node.id}`}
		>
			<Box className={classes.contentContainer}>
				<Box className={classes.leftSection}>
					<Box className={classes.toggleContainer}>
						{node.hasChildren && !isFetchingChildren && (
							<ActionIcon
								onClick={toggle}
								data-testid={`tree-node-${node.id}-collapsable`}
								className={classes.chevronButton}
							>
								<Icon name="chevronRight" className={classes.chevron} />
							</ActionIcon>
						)}
						{node.hasChildren && isFetchingChildren && (
							<SmallLoadingSpinner size="xs" />
						)}
					</Box>
					<Box className={classes.rightSection}>
						{(() => {
							const iconEl = getIcon ? getIcon(node) : defaultGetIcon(node);
							return iconEl ? (
								<Box className={classes.iconContainer}>{iconEl}</Box>
							) : null;
						})()}
						<Box className={classes.textContainer}>
							<Text
								size="sm"
								weight="semibold"
								color="text/primary/default"
								lineClamp={1}
								className={classes.textContent}
							>
								{getLabel(node)}
							</Text>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
}

/**
 * This component is used to render an entity tree. For each node it will do a catalog query to get the children
 * and render them
 */
export function EntityTreeNode({
	node,
	level,
	getLabel,
	getIcon,
	onClick,
	childFilterOperands,
}: EntityTreeNodeProps) {
	const paramsId = useParamsIdSuffixUuid();
	const [open, setOpen] = useState(false);
	const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({});

	const filters = useMemo(() => {
		if (childFilterOperands) {
			return childFilterOperands(node);
		}
		return [
			{
				field: 'entity_type',
				operator: 'exact',
				value: node.entity?.entity_type,
			},
			{
				field: 'parent_id',
				operator: 'exact',
				value: node.entity?.id,
			},
		];
	}, [childFilterOperands, node]);

	const {
		data: results,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
	} = useCatalogInfiniteList({
		filters: {
			filter: {
				operator: 'and',
				operands: filters,
			} as Filter,
			page_size: 50,
			sort:
				node.entity.entity_type === EntityType.table
					? { field: 'sort_order', order: 'asc' }
					: { field: 'title', order: 'asc' },
			prefetch_children_count: true,
		},
		options: {
			enabled: open,
		},
	});

	const isSelected = useMemo(() => {
		if ('entity' in node) {
			return paramsId === node.id;
		}
		return false;
	}, [node, paramsId]);

	const toggle = useCallback((e: React.MouseEvent) => {
		// Always stop propagation. The parent will cause a navigation and completely re-render the page.
		e.stopPropagation();
		setOpen((prev) => {
			const next = !prev;
			if (!next) {
				setGroupOpen({});
			}
			return next;
		});
	}, []);

	const children = useMemo<ISecodaEntity[]>(
		() => results?.pages.flatMap((page) => page) ?? [],
		[results]
	);

	const renderChildren = () => {
		if (!open) {
			return null;
		}

		if (node.entity.entity_type === EntityType.schema) {
			const groups = children.reduce<Record<string, ISecodaEntity[]>>(
				(acc, child) => {
					const key = (child.native_type || 'unknown').toLowerCase();
					return {
						...acc,
						[key]: [...(acc[key] || []), child],
					};
				},
				{}
			);

			const orderedKeys = [
				...TABLE_GROUP_ORDER.filter((groupKey) => groupKey in groups),
				...Object.keys(groups)
					.filter((groupKey) => !TABLE_GROUP_ORDER.includes(groupKey))
					.sort(),
			];

			const groupElements = orderedKeys.map((groupKey) => {
				const label = formatGroupLabel(groupKey);
				const isGroupOpen = groupOpen[groupKey] ?? false;
				const headerNode: CommonEntityTreeNode = {
					id: `${node.id}-${groupKey}-header`,
					hasChildren: true,
					entity: {
						id: `${node.id}-${groupKey}-entity`,
						title: label,
						title_cased: label,
						entity_type: EntityType.generic,
						icon: '',
					},
				};

				return (
					<Fragment key={`${node.id}-${groupKey}-group`}>
						<TreeNodeDisplay
							node={headerNode}
							level={level + 1}
							getLabel={() => label}
							getIcon={() => null}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
							open={isGroupOpen}
							isFetchingChildren={false}
							toggle={(e) => {
								e.stopPropagation();
								setGroupOpen((prev) => ({
									...prev,
									[groupKey]: !isGroupOpen,
								}));
							}}
							isSelected={false}
						/>
						{isGroupOpen
							? groups[groupKey].map((entity) => (
									<EntityTreeNode
										key={entity.id}
										node={{
											entity,
											id: entity.id,
											hasChildren:
												Boolean(entity.has_child_of_same_type) ||
												entity.children_count > 0,
										}}
										level={level + 2}
										onClick={onClick}
										getLabel={getLabel}
										childFilterOperands={childFilterOperands}
										getIcon={getIcon}
									/>
								))
							: null}
					</Fragment>
				);
			});

			const hasOpenGroup = orderedKeys.some((groupKey) => groupOpen[groupKey]);
			const showLoadMore = hasOpenGroup && (hasNextPage || isFetchingNextPage);

			return (
				<>
					{groupElements}
					{showLoadMore ? (
						<LoadMore
							level={level + 2}
							onClick={fetchNextPage}
							isLoading={isFetchingNextPage}
						/>
					) : null}
				</>
			);
		}

		const showLoadMore = hasNextPage || isFetchingNextPage;

		return (
			<>
				{children.map((entity) => (
					<EntityTreeNode
						key={entity.id}
						node={{
							entity: entity,
							id: entity.id,
							hasChildren:
								Boolean(entity.has_child_of_same_type) ||
								entity.children_count > 0,
						}}
						level={level + 1}
						onClick={onClick}
						getLabel={getLabel}
						childFilterOperands={childFilterOperands}
						getIcon={getIcon}
					/>
				))}
				{showLoadMore ? (
					<LoadMore
						level={level + 1}
						onClick={fetchNextPage}
						isLoading={isFetchingNextPage}
					/>
				) : null}
			</>
		);
	};

	return (
		<>
			<TreeNodeDisplay
				node={node}
				level={level}
				getLabel={getLabel}
				getIcon={getIcon}
				onClick={onClick}
				open={open}
				isFetchingChildren={isFetching}
				toggle={toggle}
				isSelected={isSelected}
			/>
			{renderChildren()}
		</>
	);
}
